(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-mh-guide]';
  var INTERACTIVE_UI_SELECTOR = [
    '[data-hotspot-target]',
    '[data-hotspot-panel]',
    '[data-step-target]',
    '[data-step-panel]',
    '[data-size-target]',
    '[data-size-panel]'
  ].join(',');
  var RUNTIME_KEY = '__mhGuideRuntimeV4';
  var stateStore = window[RUNTIME_KEY] && window[RUNTIME_KEY].state ? window[RUNTIME_KEY].state : {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function qsa(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function setPressed(buttons, activeButton) {
    buttons.forEach(function (button) {
      button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
    });
  }

  function rememberState(root, key, value) {
    if (!root || !root.id || !key || !value) return;
    if (!stateStore[root.id]) stateStore[root.id] = {};
    stateStore[root.id][key] = value;
  }

  function recalledState(root, key) {
    return root && root.id && stateStore[root.id] ? stateStore[root.id][key] : '';
  }

  function addCleanup(root, callback) {
    if (!root.__mhGuideCleanups) root.__mhGuideCleanups = [];
    root.__mhGuideCleanups.push(callback);
  }

  function listen(root, target, type, handler, options) {
    target.addEventListener(type, handler, options);
    addCleanup(root, function () {
      var capture = options === true || Boolean(options && options.capture);
      target.removeEventListener(type, handler, capture);
    });
  }

  function registerObserver(root, observer) {
    addCleanup(root, function () { observer.disconnect(); });
    return observer;
  }

  function setPanelState(panel, isActive) {
    panel.classList.toggle('is-active', isActive);
    panel.hidden = !isActive;
    panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    panel.setAttribute('data-mh-state', isActive ? 'active' : 'inactive');
    if ('inert' in panel) panel.inert = !isActive;
  }

  function centerInside(scroller, item, smooth) {
    if (!scroller || !item || typeof scroller.scrollTo !== 'function') return;
    var itemLeft = item.offsetLeft;
    var itemRight = itemLeft + item.offsetWidth;
    var visibleLeft = scroller.scrollLeft;
    var visibleRight = visibleLeft + scroller.clientWidth;
    var padding = 18;
    if (itemLeft >= visibleLeft + padding && itemRight <= visibleRight - padding) return;
    var targetLeft = itemLeft - ((scroller.clientWidth - item.offsetWidth) / 2);
    scroller.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto'
    });
  }

  function destroyRoot(root) {
    if (!root) return;
    var cleanups = root.__mhGuideCleanups || [];
    cleanups.forEach(function (cleanup) {
      try { cleanup(); }
      catch (error) { /* One failed cleanup must not block the others. */ }
    });
    root.__mhGuideCleanups = [];
    root.removeAttribute('data-mh-guide-ready');
  }

  function initReveals(root) {
    var items = qsa(root, '[data-guide-reveal]');
    if (!items.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (item) { item.classList.add('is-visible'); });
      return;
    }

    var observer = registerObserver(root, new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }));

    items.forEach(function (item) { observer.observe(item); });
  }

  function initNavigation(root) {
    var links = qsa(root, '[data-guide-nav-link]');
    var sections = qsa(root, '[data-guide-section]');
    if (!links.length || !sections.length) return;

    var linkById = {};
    var activeId = '';
    var navigationFrame = 0;
    links.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      var id = href.charAt(0) === '#' ? href.slice(1) : '';
      if (id) linkById[id] = link;

      listen(root, link, 'click', function (event) {
        var target = id ? document.getElementById(id) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        try {
          window.history.replaceState(null, '', '#' + id);
        } catch (error) {
          /* History is optional; navigation still works without it. */
        }
      });
    });

    function activate(id, smoothCenter) {
      if (!linkById[id] || activeId === id) return;
      activeId = id;
      links.forEach(function (link) {
        if (link === linkById[id]) {
          link.setAttribute('aria-current', 'true');
          centerInside(link.closest('.mh-guide__nav-scroller'), link, smoothCenter);
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }

    function updateFromScroll() {
      navigationFrame = 0;
      if (!root.isConnected) return;
      var nav = root.querySelector('.mh-guide__local-nav');
      var navHeight = nav ? nav.getBoundingClientRect().height : 0;
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var activationLine = navHeight + Math.min(170, viewport * 0.24);
      var winner = sections[0];

      sections.forEach(function (section) {
        if (section.getBoundingClientRect().top <= activationLine) winner = section;
      });

      activate(winner.id, false);
    }

    function requestNavigationUpdate() {
      if (navigationFrame) return;
      navigationFrame = window.requestAnimationFrame(updateFromScroll);
    }

    listen(root, window, 'scroll', requestNavigationUpdate, { passive: true });
    listen(root, window, 'resize', requestNavigationUpdate, { passive: true });
    addCleanup(root, function () {
      if (navigationFrame) window.cancelAnimationFrame(navigationFrame);
    });
    updateFromScroll();
  }

  function initProgress(root) {
    var progress = root.querySelector('.mh-guide__progress');
    if (!progress) return;
    var progressFrame = 0;
    var lastRatio = -1;

    function update() {
      progressFrame = 0;
      if (!root.isConnected) return;
      var rect = root.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var total = Math.max(1, root.offsetHeight - viewport);
      var travelled = Math.min(total, Math.max(0, -rect.top));
      var ratio = Math.min(1, Math.max(0, travelled / total));
      if (Math.abs(ratio - lastRatio) < 0.0005) return;
      lastRatio = ratio;
      progress.style.transform = 'scaleX(' + ratio.toFixed(5) + ')';
    }

    function requestUpdate() {
      if (progressFrame) return;
      progressFrame = window.requestAnimationFrame(update);
    }

    listen(root, window, 'scroll', requestUpdate, { passive: true });
    listen(root, window, 'resize', requestUpdate, { passive: true });
    listen(root, window, 'load', requestUpdate, { once: true });
    addCleanup(root, function () {
      if (progressFrame) window.cancelAnimationFrame(progressFrame);
    });
    if ('ResizeObserver' in window) {
      var resizeObserver = registerObserver(root, new ResizeObserver(requestUpdate));
      resizeObserver.observe(root);
    }
    update();
  }

  function initReactiveCards(root) {
    qsa(root, '[data-guide-reactive]').forEach(function (card) {
      listen(root, card, 'pointermove', function (event) {
        if (!finePointer.matches || event.pointerType === 'touch') return;
        var rect = card.getBoundingClientRect();
        card.style.setProperty('--mh-pointer-x', ((event.clientX - rect.left) / rect.width * 100).toFixed(2) + '%');
        card.style.setProperty('--mh-pointer-y', ((event.clientY - rect.top) / rect.height * 100).toFixed(2) + '%');
      }, { passive: true });

      listen(root, card, 'pointerdown', function (event) {
        if (event.pointerType !== 'touch') return;
        if (card.closest('[data-guide-drag]')) return;
        var rect = card.getBoundingClientRect();
        var pulse = document.createElement('span');
        pulse.className = 'mh-guide__tap-pulse';
        pulse.style.left = (event.clientX - rect.left) + 'px';
        pulse.style.top = (event.clientY - rect.top) + 'px';
        card.appendChild(pulse);
        card.classList.add('is-active');
        window.setTimeout(function () {
          pulse.remove();
          card.classList.remove('is-active');
        }, 620);
      }, { passive: true });
    });
  }

  function initTilt(root) {
    if (reduceMotion.matches) return;

    qsa(root, '[data-guide-tilt]').forEach(function (card) {
      var frame = 0;

      function reset() {
        card.style.setProperty('--mh-tilt-x', '0deg');
        card.style.setProperty('--mh-tilt-y', '0deg');
      }

      listen(root, card, 'pointermove', function (event) {
        if (!finePointer.matches || event.pointerType === 'touch') return;
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(function () {
          var rect = card.getBoundingClientRect();
          var x = (event.clientX - rect.left) / rect.width - 0.5;
          var y = (event.clientY - rect.top) / rect.height - 0.5;
          card.style.setProperty('--mh-tilt-x', (-y * 4).toFixed(2) + 'deg');
          card.style.setProperty('--mh-tilt-y', (x * 5).toFixed(2) + 'deg');
        });
      }, { passive: true });

      listen(root, card, 'pointerleave', reset, { passive: true });
      listen(root, card, 'blur', reset, true);
    });
  }

  function activateHotspot(root, target) {
    var buttons = qsa(root, '[data-hotspot-target]');
    var panels = qsa(root, '[data-hotspot-panel]');
    if (!buttons.length || !panels.length) return false;
    var activeButton = buttons.find(function (button) {
      return button.getAttribute('data-hotspot-target') === target;
    });
    var activePanel = panels.find(function (panel) {
      return panel.getAttribute('data-hotspot-panel') === target;
    });
    if (!activeButton || !activePanel) return false;

    buttons.forEach(function (button) {
      button.setAttribute(
        'aria-pressed',
        button.getAttribute('data-hotspot-target') === target ? 'true' : 'false'
      );
    });
    panels.forEach(function (panel) {
      setPanelState(panel, panel === activePanel);
    });
    rememberState(root, 'hotspot', target);
    return true;
  }

  function initHotspots(root) {
    var buttons = qsa(root, '[data-hotspot-target]');
    var panels = qsa(root, '[data-hotspot-panel]');
    if (!buttons.length || !panels.length) return;
    var remembered = recalledState(root, 'hotspot');
    var initial = buttons.find(function (button) {
      return remembered && button.getAttribute('data-hotspot-target') === remembered;
    }) || buttons.find(function (button) {
      return button.getAttribute('aria-pressed') === 'true';
    }) || buttons[0];
    activateHotspot(root, initial.getAttribute('data-hotspot-target'));
  }

  function activateStep(root, target, userInitiated) {
    var buttons = qsa(root, '[data-step-target]');
    var panels = qsa(root, '[data-step-panel]');
    if (!buttons.length || !panels.length) return false;
    var activeButton = buttons.find(function (button) {
      return button.getAttribute('data-step-target') === target;
    });
    var activePanel = panels.find(function (panel) {
      return panel.getAttribute('data-step-panel') === target;
    });
    if (!activeButton || !activePanel) return false;

    setPressed(buttons, activeButton);
    buttons.forEach(function (button) {
      var isActive = button === activeButton;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach(function (panel) {
      setPanelState(panel, panel === activePanel);
    });
    rememberState(root, 'step', target);

    if (userInitiated && window.innerWidth < 990) {
      centerInside(activeButton.closest('.mh-guide__step-list'), activeButton, true);
    }
    return true;
  }

  function initSteps(root) {
    var buttons = qsa(root, '[data-step-target]');
    var panels = qsa(root, '[data-step-panel]');
    if (!buttons.length || !panels.length) return;
    var remembered = recalledState(root, 'step');
    var initial = buttons.find(function (button) {
      return remembered && button.getAttribute('data-step-target') === remembered;
    }) || buttons.find(function (button) {
      return button.getAttribute('aria-pressed') === 'true';
    }) || buttons[0];
    activateStep(root, initial.getAttribute('data-step-target'), false);
  }

  function activateSize(component, target, userInitiated) {
    var buttons = qsa(component, '[data-size-target]');
    var panels = qsa(component, '[data-size-panel]');
    var stage = component.querySelector('[data-size-stage]');
    if (!stage || !buttons.length || !panels.length) return false;

    var nextIndex = buttons.findIndex(function (button) {
      return button.getAttribute('data-size-target') === target;
    });
    var currentIndex = buttons.findIndex(function (button) {
      return button.getAttribute('aria-pressed') === 'true';
    });
    var activeButton = buttons[nextIndex];
    var activePanel = panels.find(function (panel) {
      return panel.getAttribute('data-size-panel') === target;
    });
    if (nextIndex < 0 || !activeButton || !activePanel) return false;

    stage.setAttribute('data-size-direction', nextIndex < Math.max(0, currentIndex) ? 'previous' : 'next');
    setPressed(buttons, activeButton);
    buttons.forEach(function (button) {
      var isActive = button === activeButton;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
    });
    panels.forEach(function (panel) {
      setPanelState(panel, panel === activePanel);
    });
    rememberState(component.closest(ROOT_SELECTOR), 'size', target);

    if (userInitiated) {
      try { activeButton.focus({ preventScroll: true }); }
      catch (error) { activeButton.focus(); }
    }
    return true;
  }

  function moveSize(component, direction) {
    var buttons = qsa(component, '[data-size-target]');
    if (buttons.length < 2) return;
    var activeIndex = buttons.findIndex(function (button) {
      return button.getAttribute('aria-pressed') === 'true';
    });
    activeIndex = Math.max(0, activeIndex);
    var nextIndex = Math.max(0, Math.min(buttons.length - 1, activeIndex + direction));
    if (nextIndex === activeIndex) return;
    activateSize(component, buttons[nextIndex].getAttribute('data-size-target'), true);
  }

  function initSizes(root) {
    qsa(root, '[data-guide-sizes]').forEach(function (component) {
      var buttons = qsa(component, '[data-size-target]');
      var panels = qsa(component, '[data-size-panel]');
      var stage = component.querySelector('[data-size-stage]');
      if (!stage || !panels.length) return;

      var remembered = recalledState(root, 'size');
      var initial = buttons.find(function (button) {
        return remembered && button.getAttribute('data-size-target') === remembered;
      }) || buttons.find(function (button) {
        return button.getAttribute('aria-pressed') === 'true';
      });
      if (initial) activateSize(component, initial.getAttribute('data-size-target'), false);
      else panels.forEach(function (panel, index) { setPanelState(panel, index === 0); });
      if (panels.length < 2 || buttons.length < 2) return;

      var pointerStartX = 0;
      var pointerStartY = 0;
      var pointerId = null;

      listen(root, stage, 'pointerdown', function (event) {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        pointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      }, { passive: true });

      listen(root, stage, 'pointerup', function (event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var distanceX = event.clientX - pointerStartX;
        var distanceY = event.clientY - pointerStartY;
        pointerId = null;
        if (Math.abs(distanceX) < 44 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
        moveSize(component, distanceX < 0 ? 1 : -1);
      }, { passive: true });

      listen(root, stage, 'pointercancel', function () { pointerId = null; }, { passive: true });
    });
  }

  function initVideos(root) {
    var videos = qsa(root, '[data-guide-video-autoplay] video');
    if (!videos.length) return;

    videos.forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      if (reduceMotion.matches) {
        video.pause();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
        return;
      }

      var observer = registerObserver(root, new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            var promise = video.play();
            if (promise && typeof promise.catch === 'function') promise.catch(function () {});
          } else {
            video.pause();
          }
        });
      }, { threshold: [0, 0.35, 0.75] }));
      observer.observe(video);
    });
  }

  function initDragScroll(root) {
    qsa(root, '[data-guide-drag]').forEach(function (track) {
      var isDown = false;
      var moved = false;
      var startX = 0;
      var startScroll = 0;
      var pointerId = null;
      var dragFrame = 0;
      var pendingScroll = 0;

      listen(root, track, 'pointerdown', function (event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        isDown = true;
        moved = false;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture(pointerId);
      });

      listen(root, track, 'pointermove', function (event) {
        if (!isDown || event.pointerId !== pointerId) return;
        var distance = event.clientX - startX;
        if (Math.abs(distance) > 5) moved = true;
        pendingScroll = startScroll - distance;
        if (!dragFrame) {
          dragFrame = window.requestAnimationFrame(function () {
            track.scrollLeft = pendingScroll;
            dragFrame = 0;
          });
        }
        if (moved) event.preventDefault();
      });

      function finish(event) {
        if (!isDown || (event && event.pointerId !== pointerId)) return;
        isDown = false;
        if (dragFrame) {
          window.cancelAnimationFrame(dragFrame);
          dragFrame = 0;
          track.scrollLeft = pendingScroll;
        }
        track.classList.remove('is-dragging');
        if (pointerId !== null && track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
        window.setTimeout(function () { moved = false; }, 0);
        pointerId = null;
      }

      listen(root, track, 'pointerup', finish);
      listen(root, track, 'pointercancel', finish);
      listen(root, track, 'lostpointercapture', finish);

      listen(root, track, 'click', function (event) {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    });
  }

  function initCarousels(root) {
    qsa(root, '[data-guide-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-guide-carousel-track]');
      var previous = carousel.querySelector('[data-guide-carousel-prev]');
      var next = carousel.querySelector('[data-guide-carousel-next]');
      if (!track || !previous || !next) return;

      var updateFrame = 0;

      function updateButtons() {
        updateFrame = 0;
        var maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = maximum < 2 || track.scrollLeft <= 2;
        next.disabled = maximum < 2 || track.scrollLeft >= maximum - 2;
      }

      function requestButtonUpdate() {
        if (updateFrame) return;
        updateFrame = window.requestAnimationFrame(updateButtons);
      }

      function cardStep() {
        var card = track.querySelector('.mh-guide__product');
        if (!card) return Math.max(260, track.clientWidth * 0.8);
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function move(direction) {
        track.scrollBy({
          left: direction * cardStep(),
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
      }

      listen(root, previous, 'click', function () { move(-1); });
      listen(root, next, 'click', function () { move(1); });
      listen(root, track, 'scroll', requestButtonUpdate, { passive: true });
      listen(root, window, 'resize', requestButtonUpdate, { passive: true });
      addCleanup(root, function () {
        if (updateFrame) window.cancelAnimationFrame(updateFrame);
      });
      if ('ResizeObserver' in window) {
        var resizeObserver = registerObserver(root, new ResizeObserver(requestButtonUpdate));
        resizeObserver.observe(track);
      }
      updateButtons();
    });
  }

  function initFaq(root) {
    qsa(root, '[data-guide-faq] details').forEach(function (details) {
      listen(root, details, 'toggle', function () {
        if (!details.open) return;
        qsa(root, '[data-guide-faq] details').forEach(function (other) {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  function handleDelegatedClick(event) {
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;

    var hotspotButton = target.closest('[data-hotspot-target]');
    if (hotspotButton) {
      var hotspotRoot = hotspotButton.closest(ROOT_SELECTOR);
      if (hotspotRoot) activateHotspot(hotspotRoot, hotspotButton.getAttribute('data-hotspot-target'));
      return;
    }

    var stepButton = target.closest('[data-step-target]');
    if (stepButton) {
      var stepRoot = stepButton.closest(ROOT_SELECTOR);
      if (stepRoot) activateStep(stepRoot, stepButton.getAttribute('data-step-target'), true);
      return;
    }

    var sizeButton = target.closest('[data-size-target]');
    if (sizeButton) {
      var component = sizeButton.closest('[data-guide-sizes]');
      if (component) activateSize(component, sizeButton.getAttribute('data-size-target'), true);
    }
  }

  function handleDelegatedFocus(event) {
    var button = event.target && event.target.closest ? event.target.closest('[data-hotspot-target]') : null;
    if (!button) return;
    var root = button.closest(ROOT_SELECTOR);
    if (root) activateHotspot(root, button.getAttribute('data-hotspot-target'));
  }

  function handleDelegatedHover(event) {
    if (!finePointer.matches) return;
    var button = event.target && event.target.closest ? event.target.closest('[data-hotspot-target]') : null;
    if (!button || (event.relatedTarget && button.contains(event.relatedTarget))) return;
    var root = button.closest(ROOT_SELECTOR);
    if (root) activateHotspot(root, button.getAttribute('data-hotspot-target'));
  }

  function handleDelegatedKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') return;
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;

    var stepButton = target.closest('[data-step-target]');
    if (stepButton) {
      var root = stepButton.closest(ROOT_SELECTOR);
      if (!root) return;
      var stepButtons = qsa(root, '[data-step-target]');
      var stepIndex = stepButtons.indexOf(stepButton);
      if (stepIndex < 0) return;
      event.preventDefault();
      if (event.key === 'Home') stepIndex = 0;
      else if (event.key === 'End') stepIndex = stepButtons.length - 1;
      else if (event.key === 'ArrowRight') stepIndex = (stepIndex + 1) % stepButtons.length;
      else stepIndex = (stepIndex - 1 + stepButtons.length) % stepButtons.length;
      stepButtons[stepIndex].focus();
      activateStep(root, stepButtons[stepIndex].getAttribute('data-step-target'), true);
      return;
    }

    var sizeButton = target.closest('[data-size-target]');
    if (!sizeButton) return;
    var component = sizeButton.closest('[data-guide-sizes]');
    if (!component) return;
    var sizeButtons = qsa(component, '[data-size-target]');
    var sizeIndex = sizeButtons.indexOf(sizeButton);
    if (sizeIndex < 0) return;
    event.preventDefault();
    if (event.key === 'Home') sizeIndex = 0;
    else if (event.key === 'End') sizeIndex = sizeButtons.length - 1;
    else if (event.key === 'ArrowRight') sizeIndex = Math.min(sizeButtons.length - 1, sizeIndex + 1);
    else sizeIndex = Math.max(0, sizeIndex - 1);
    activateSize(component, sizeButtons[sizeIndex].getAttribute('data-size-target'), true);
  }

  function repairInteractiveState(root) {
    if (!root || !root.isConnected) return;
    initHotspots(root);
    initSteps(root);
    qsa(root, '[data-guide-sizes]').forEach(function (component) {
      var buttons = qsa(component, '[data-size-target]');
      var panels = qsa(component, '[data-size-panel]');
      var remembered = recalledState(root, 'size');
      var initial = buttons.find(function (button) {
        return remembered && button.getAttribute('data-size-target') === remembered;
      }) || buttons.find(function (button) {
        return button.getAttribute('aria-pressed') === 'true';
      });
      if (initial) activateSize(component, initial.getAttribute('data-size-target'), false);
      else panels.forEach(function (panel, index) { setPanelState(panel, index === 0); });
    });
  }

  function initRoot(root) {
    if (!root || root.getAttribute('data-mh-guide-ready') === 'true') return;
    if (root.__mhGuideCleanups && root.__mhGuideCleanups.length) destroyRoot(root);
    root.__mhGuideCleanups = [];
    root.setAttribute('data-mh-guide-ready', 'true');
    initReveals(root);
    initNavigation(root);
    initProgress(root);
    initReactiveCards(root);
    initTilt(root);
    initHotspots(root);
    initSteps(root);
    initSizes(root);
    initVideos(root);
    initDragScroll(root);
    initCarousels(root);
    initFaq(root);
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initRoot(context);
    qsa(context, ROOT_SELECTOR).forEach(initRoot);
  }

  function destroyAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches(ROOT_SELECTOR)) destroyRoot(context);
    qsa(context, ROOT_SELECTOR).forEach(destroyRoot);
  }

  function handleReady() {
    initAll(document);
  }

  function handleSectionLoad(event) {
    initAll(event.target);
  }

  function handleSectionUnload(event) {
    destroyAll(event.target);
  }

  function handleBlockSelect(event) {
    var root = event.target && event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
    if (!root) return;
    if (root.getAttribute('data-mh-guide-ready') === 'true') repairInteractiveState(root);
    else initRoot(root);
  }

  var pendingRoots = [];
  var mutationQueued = false;
  var runtimeActive = true;

  function flushPendingRoots() {
    mutationQueued = false;
    if (!runtimeActive) return;
    var roots = pendingRoots.slice();
    pendingRoots = [];
    roots.forEach(function (queuedRoot) {
      if (!queuedRoot.isConnected) return;
      if (queuedRoot.getAttribute('data-mh-guide-ready') === 'true') destroyRoot(queuedRoot);
      initRoot(queuedRoot);
    });
  }

  function queueRootRefresh(root) {
    if (!root || pendingRoots.indexOf(root) !== -1) return;
    pendingRoots.push(root);
    if (mutationQueued) return;
    mutationQueued = true;
    Promise.resolve().then(flushPendingRoots);
  }

  function isInternalPulse(node) {
    return node && node.nodeType === 1 && node.classList.contains('mh-guide__tap-pulse');
  }

  function containsInteractiveUI(node) {
    if (!node || node.nodeType !== 1 || isInternalPulse(node)) return false;
    if (node.matches && node.matches(INTERACTIVE_UI_SELECTOR)) return true;
    return Boolean(node.querySelector && node.querySelector(INTERACTIVE_UI_SELECTOR));
  }

  var editorObserver = 'MutationObserver' in window ? new MutationObserver(function (records) {
    records.forEach(function (record) {
      var refreshExistingRoot = false;
      Array.prototype.forEach.call(record.addedNodes || [], function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(ROOT_SELECTOR)) {
          queueRootRefresh(node);
          return;
        }

        var nestedRoots = qsa(node, ROOT_SELECTOR);
        if (nestedRoots.length) {
          nestedRoots.forEach(queueRootRefresh);
          return;
        }

        if (containsInteractiveUI(node)) refreshExistingRoot = true;
      });

      /*
       * Shopify may replace only the controls inside an existing section. In
       * that case the root must be rebuilt. Ordinary additions (lazy images,
       * videos, editor helpers or decorative nodes) must not restart the guide.
       */
      if (!refreshExistingRoot || !record.target.closest) return;
      var existingRoot = record.target.closest(ROOT_SELECTOR);
      if (existingRoot) queueRootRefresh(existingRoot);
    });
  }) : null;

  var previousRuntime = window[RUNTIME_KEY];
  if (previousRuntime && typeof previousRuntime.destroy === 'function') {
    previousRuntime.destroy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleReady, { once: true });
  } else {
    handleReady();
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:block:select', handleBlockSelect);
  /*
   * Run interactive controls in capture phase. Shopify's theme editor and
   * some theme components may stop a tab-button click while it bubbles. In
   * capture phase the guide receives the action first, so XS-S / M-L and the
   * other tabbed controls remain usable inside the editor as well as on the
   * storefront.
   */
  document.addEventListener('click', handleDelegatedClick, true);
  document.addEventListener('focusin', handleDelegatedFocus);
  document.addEventListener('mouseover', handleDelegatedHover);
  document.addEventListener('keydown', handleDelegatedKeydown);
  if (editorObserver) editorObserver.observe(document.documentElement, { childList: true, subtree: true });

  window[RUNTIME_KEY] = {
    state: stateStore,
    init: initAll,
    destroy: function () {
      document.removeEventListener('DOMContentLoaded', handleReady);
      document.removeEventListener('shopify:section:load', handleSectionLoad);
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
      document.removeEventListener('shopify:block:select', handleBlockSelect);
      document.removeEventListener('click', handleDelegatedClick, true);
      document.removeEventListener('focusin', handleDelegatedFocus);
      document.removeEventListener('mouseover', handleDelegatedHover);
      document.removeEventListener('keydown', handleDelegatedKeydown);
      if (editorObserver) editorObserver.disconnect();
      runtimeActive = false;
      mutationQueued = false;
      pendingRoots = [];
      destroyAll(document);
    }
  };
})();
